// context/ToastContext.tsx
import React, { useState, createContext, useContext, ReactNode } from "react";
import { AnimatePresence } from "framer-motion";
import { ToastProps } from "@components/common/Toast/Toast.props";
import Toast from "@components/common/Toast/Toast";

type ShowToastProps = Omit<ToastProps, "id">;

interface ToastContextProps {
	showToast: (toast: ShowToastProps) => void;
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined);

export const useToast = () => {
	const context = useContext(ToastContext);
	if (!context) {
		throw new Error("useToast must be used within a ToastProvider");
	}
	return context;
};

const positionsStyles = {
	"top-left": "z-[999] top-4 left-4",
	"top-center": "z-[999] top-4 left-1/2 transform -translate-x-1/2",
	"top-right": "z-[999] top-4 right-4",
	"center-left": "z-[999] top-1/2 left-4 transform -translate-y-1/2",
	center:
		"z-[999] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
	"center-right": "z-[999] top-1/2 right-4 transform -translate-y-1/2",
	"bottom-left": "z-[999] bottom-4 left-4",
	"bottom-center": "z-[999] bottom-4 left-1/2 transform -translate-x-1/2",
	"bottom-right": "z-[999] bottom-4 right-4",
};

const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	const [toasts, setToasts] = useState<ToastProps[]>([]);

	const showToast = (toast: ShowToastProps) => {
		const id = Date.now().toString();
		setToasts((prev) => [...prev, { ...toast, id }]);
	};

	const removeToast = (id: string) => {
		setToasts((prev) => prev.filter((toast) => toast.id !== id));
	};

	return (
		<ToastContext.Provider value={{ showToast }}>
			{children}
			<AnimatePresence>
				{toasts.map((toast) => (
					<div
						key={toast.id}
						className={`fixed ${positionsStyles[toast.position]}`}
					>
						<Toast {...toast} onClose={() => removeToast(toast.id)} />
					</div>
				))}
			</AnimatePresence>
		</ToastContext.Provider>
	);
};

export default ToastProvider;
